<template>
	<v-container>
		<v-window v-model="step">
			<v-row class="pl-0">
				<v-col cols="12" class="text-white pb-6 pl-0">
					Please enter a new password and select your two-factor authentication preference.
				</v-col>
			</v-row>
			<v-window-item :value="'newPassword'">
				<ValidationObserver ref="newpw" v-slot="{ invalid, validated, passes }">
					<v-form>
						<ValidationProvider
							v-slot="{ errors }"
							name="password"
							rules="required|min:12|verify_password"
							vid="newPassword"
						>
							<v-row>
								<v-col cols="12">
									<v-text-field
										v-model="password"
										:error-messages="errors"
										placeholder="Enter password"
										:type="e1 ? 'password' : 'text'"
										counter
										filled
									>
										<template v-slot:append>
											<eye-icon v-if="e1" style="cursor: pointer;" @click="() => (e1 = !e1)"></eye-icon>
											<eyeoff-icon v-else style="cursor: pointer;" @click="() => (e1 = !e1)"></eyeoff-icon>
										</template>
									</v-text-field>
								</v-col>
							</v-row>
							
						</ValidationProvider>
						<ValidationProvider
							v-slot="{ errors }"
							name="confirmPassword"
							rules="required|min:12|verify_password|confirmed:newPassword"
						>
							<v-text-field
								v-model="confirmPassword"
								placeholder="Confirm password"
								:error-messages="errors"
								:type="e1 ? 'password' : 'text'"
								counter
								filled
								required
								@keyup.enter="passes(submit)"
							/>
						</ValidationProvider>
						<div v-if="err.length" class="error-callback">
							<ul>
								<li v-for="error in err" :key="error">{{ error }}</li>
							</ul>
						</div>

						<v-row class="pl-4 pb-0">
							<v-col cols="12" class="d-flex pl-0 pb-0">
								<v-icon color="white" @click="toggleMfaInfo">mdi-information</v-icon>
								<span class="white--text pl-1">Choose your two-factor authentication method:</span>
							</v-col>
						</v-row>
						<v-row class="pl-4">
							<v-col cols="12" class="pt-0 pl-0">
								<v-radio-group v-model="mfaType" :disabled="loading">
									<v-radio color="white" class="pb-2" label="Enable two-factor authentication via text message" value="sms"></v-radio>
									<v-radio color="white" label="I don't want to enable two-factor authentication right now" value="none"></v-radio>
								</v-radio-group>
							</v-col>
						</v-row>

					</v-form>

					<v-layout justify-end>
						<v-btn
							class="white--text"
							color="#3db0ad"
							:loading="loading"
							:disabled="invalid || !validated || loading"
							@click.prevent="passes(submit)"
						>Next</v-btn>
					</v-layout>
					<v-dialog v-model="confirmNoMfa" persistent class="mfa-nag">
						<v-card width="500" class="pa-4">
							<v-row>
								<v-col cols="12">
									<v-icon color="red">
										mdi-alert
									</v-icon>
									Not recommended!
								</v-col>
							</v-row>
							<v-row>
								<v-col cols="12">
									For your account security, we strongly advise that you do not remove two factor authentication. Are you sure you want to proceed?
								</v-col>
							</v-row>
							<v-row>
								<v-col cols="3">	
									<v-btn 
										color="#3db0ad" 
										class="white--text"
										size="small"
										variant="flat"
										@click="handleMfaConfig('back')"
									>
										NO
									</v-btn>
								</v-col>
								<v-spacer/>
								<v-col cols="3">	
									<v-btn 
										color="red" 
										class="white--text"
										size="small"
										variant="flat"
										:disabled="invalid || !validated || loading"
										@click="handleMfaConfig('noMfa')"
									>
										YES
									</v-btn>
								</v-col>
							</v-row>
						</v-card>
					</v-dialog>
				</ValidationObserver>
			</v-window-item>
			
			<v-window-item :value="'userDetails'">
				<v-form  ref="userDetails" v-model="userDetailsValid">
					<v-text-field v-model="editedItem.firstName" 
						:type="'text'"
						class="remove-padding"
						filled 
						required
						placeholder="First Name" 
						:rules="nameRules">
					</v-text-field>
					<v-text-field 
						v-model="editedItem.lastName"
						placeholder="Last Name" 
						:type="'text'"
						class="remove-padding"
						filled 
						required
						:rules="nameRules"></v-text-field>
		
					<v-col cols="12" class="pa-0">
            <v-phone-input ref="phoneInput" v-model="editedItem.mobile" :disabled="existingVerifiedNumberAvailable" label="Mobile Number" />
          </v-col>
					<v-col cols="12" class="pa-0">
						<v-text-field v-model="editedItem.telephone" placeholder="Alternative Phone Number" filled></v-text-field>
					</v-col>
				</v-form>
				<v-layout justify-space-between>
					<v-spacer/>
					<v-btn
						class="white--text"
						color="third"
						:loading="loader"
						:disabled="loader"
						@click.prevent="enter"
					>submit</v-btn>
				</v-layout>
			</v-window-item>


			<v-window-item :value="'setupMfa'">
				<SetupSms
					:user="user"
					@phoneVerified="handleExistingVerifiedNumber"
					@back="handleBack"
					@proceed="handleProceed"
				/>
			</v-window-item>

		</v-window>
		<v-dialog v-model="displayMfaInfo" persistent class="mfa-nag">
			<v-card width="500" class="pa-4">
				<v-row>
					<v-col cols="12">
						Two factor authentication is highly recommended to protect the security of your account.
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="12">
						Choose "Enable two-factor authentication via text message" if you would prefer to recieve a login confirmation code on your mobile phone.
					</v-col>
				</v-row>
				<v-row>
					<v-spacer/>
					<v-col cols="3">	
						<v-btn 
							color="red" 
							class="white--text"
							size="small"
							variant="flat"
							@click="toggleMfaInfo"
						>
							Dismiss
						</v-btn>
					</v-col>
				</v-row>
			</v-card>
		</v-dialog>

		
	</v-container>
</template>

<script>

	import SetupSms from "./setupSms.vue"

	import Auth from "@aws-amplify/auth";
	import API from "@aws-amplify/api";
	import Cookies from 'js-cookie';

	const API_NAME = "PVAPI";

	export default {
		components: {
			SetupSms
		},
		props: {
			user: {
				type: Object,
				default: () => ({})
			},
			response: { type: Function, default: null }
		},
		data: () => ({
			step: 'newPassword',
			loading: false,
			loader: false,
			e1: true,
			err: [],
			password: "",
			confirmPassword: "",
			profile: {},
			editedItem: {
				firstName: "",
				lastName: "",
				photo: "",
				telephone: "",
				mobile: ""
			},
			mfaType: 'sms',
			userDetailsValid: false,
			nameRules: [
        v => !!v || 'This field is required',
        v => (v && v.length >= 2) || 'Must be at least 2 characters!',
      ],
			displayMfaInfo: false,
			confirmNoMfa: false,
			existingVerifiedNumberAvailable: false
		}),

		watch: {
			confirmPassword: function() {
				this.clearErrors();
			},
			password: function() {
				this.clearErrors();
			},
			mfaType(val) {
				if(val == 'none') {
					this.confirmNoMfa = true
				}
			}
		},

		methods: {
			handleBack() {
				this.loading = false
				this.$emit('back')
			},

			handleProceed() {
				this.step = 'userDetails'
			},

			handleMfaConfig(mfa) {
				if(mfa == 'back') {
					this.confirmNoMfa = false
					this.mfaType = 'sms'
				}
				else if(mfa == 'noMfa') {
					this.confirmNoMfa = false
					this.submit()
				}
			},

			handleExistingVerifiedNumber(num) {
				if(num && num.length > 0) {
					this.editedItem.mobile = num
					this.existingVerifiedNumberAvailable = true
				}
				
			},

			toggleMfaInfo() {
				this.displayMfaInfo = !this.displayMfaInfo
			},

			handleNextStage() {
				if(this.mfaType == 'sms') {
					this.step = 'setupMfa'
				}
				else if (this.mfaType == 'none') {
					this.step = 'userDetails'
				}

				this.getUser()
			},

			submit() {
				this.loading = true;
				try {
					Auth.completeNewPassword(this.user, this.password)
					.then(res => {
						this.user = res
						
						this.getUser().then(() => {
							this.handleNextStage()
						})
					})
					.catch(() => {
						this.loading = false
					});
				}
				catch {
					this.loading = false
				}
				
			},
			async getUser() {
				const session = await Auth.currentSession();
				const token = session.getAccessToken().getJwtToken();
				const email = this.user.challengeParam.userAttributes.email;
				const res = await API.get(API_NAME, `/user/${email}`, {
					headers: {
						Authorization: token
					}
				});
				this.profile = res;
				return;
			},
			async enter() {
				this.loader = true;

				this.$refs.userDetails.validate()
				// eslint-disable-next-line no-console
				console.log("form validated")

				if(!this.userDetailsValid) {
					this.loader = false;
					// eslint-disable-next-line no-console
					console.log("user details are invalid!");
					return;
				} 

				const session = await Auth.currentSession();
				const token = session.getAccessToken().getJwtToken();
				const email = this.user.challengeParam.userAttributes.email;
				const id = this.profile.Org.id;
				const payload = this.editedItem

				if(this.existingVerifiedNumberAvailable) {
					delete payload['mobile']
				}

				await API.put(API_NAME, `/org/${id}/users/${email}`, {
					body: this.editedItem,
					headers: {
						Authorization: token
					}
				});

				// set our cookies for auth!
				Auth.currentSession().then(async session => {
							const token = session.refreshToken.token
							const domain = process.env.VUE_APP_cookie_domain || 'cameramatics.com'
							const name = process.env.VUE_APP_cookie_name || 'CameraMaticsToken'
							const accessToken = session.accessToken.jwtToken
							// eslint-disable-next-line no-console
							console.log(domain)
							// eslint-disable-next-line no-console
							console.log(name)

							Cookies.set(name, token, { expires: 7, domain: domain })
							Cookies.set('accessToken', accessToken, { expires: 7, domain: domain })
							this.response(this.user)
				}).catch(err => {
					// eslint-disable-next-line no-console
					console.log('could not retrieve session...', err)
					this.response(this.user)
				})
			},
			clearErrors() {
				if (this.err.length > 0) {
					this.err = [];
				}
			}
		}
	};
</script>

<style lang="scss">

  .v-phone-input__country.v-input {
    min-width: 0;
    max-width: 4rem;
    min-width: 0;
    text-align: center;
    padding-right: 5px;
	}
	.text-white {
		color: white;
	}
</style>